import { DEFAULT_EARLIEST, DEFAULT_LATEST } from 'constants/dateLimits';
import foreignCoOwnersModel from 'forms/sections/foreign/activities/shared/foreignCoOwners';
import { ValidateJSModel, ValidateJSOptions } from 'forms/shared/types/validatejs';
import { REQUIRED, RELINQUISHED_DATE_TOO_EARLY } from 'constants/errors';
import currencyModelValidator from 'forms/shared/validators/models/currency';
import { isPVQForm } from 'helpers/form';
import { hasYesOrNo } from 'models/validate';
import {
  foreignInterestOwnerOptions, foreignFinancialInterestOptions, foreignRealEstateOptions, foreignFinancialInterestHowAcquiredOptions,
  foreignFinancialInterestControllerOptions,
  foreignInterestCoOwnerOptions,
  REALESTATE,
  anotherPerson,
  companyOrOrg,
  foreignFinancialInterestHowRelinquishedOptions,
  OTHR,
  FINANCIAL_FOREIGN_INTEREST_VALUES
} from 'constants/enums/foreignActivityOptions';
import { YES, NO } from 'constants/values';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import locationStreetCityCountryModelValidator from 'forms/shared/validators/models/location/models/streetCityCountry';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import { isSafeUnitedStates } from 'helpers/locations';
import { setToStartOfMonth } from 'helpers/date';
import { DeepSafePartial } from 'types/types';
import { ForeignActivitiesDirectItemModel } from '../types';

type Attributes = DeepSafePartial<ForeignActivitiesDirectItemModel>;

const isPvqForeignItem = (
  attributes: Attributes,
  options: ValidateJSOptions,
) => (isPVQForm(options?.formType)
  && FINANCIAL_FOREIGN_INTEREST_VALUES.includes(attributes?.FinancialInterestType?.value || ''));

const isPvqForeignItemStillOwned = (
  attributes: Attributes,
  options: ValidateJSOptions,
) => (isPvqForeignItem(attributes, options)
  && attributes?.ForeignInterestStillOwned?.value === YES);

const isPvqForeignItemNotStillOwned = (
  attributes: Attributes,
  options: ValidateJSOptions,
) => (isPvqForeignItem(attributes, options)
  && attributes?.ForeignInterestStillOwned?.value === NO);

const isPvqForeignItemStillOwnedByPerson = (
  attributes: Attributes,
  options: ValidateJSOptions
) => (isPvqForeignItem(attributes, options)
  && attributes?.ForeignInterestControl?.value === anotherPerson);

const isPvqForeignItemStillOwnedByCompany = (
  attributes: Attributes,
  options: ValidateJSOptions,
) => (isPvqForeignItem(attributes, options)
  && attributes?.ForeignInterestControl?.value === companyOrOrg);

export const foreignActivitiesDirectItemValidator: ValidateJSModel<ForeignActivitiesDirectItemModel> = {
  InterestTypes: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {}; // not used in pvq
    }

    return {
      presence: true,
      array: {
        validator: { presence: true },
        length: { minimum: 1, message: REQUIRED },
      }
    };
  },
  InterestType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {}; // not use in pvq
    }
    return { hasValue: true };
  },

  Acquired: (value, attributes, attributeName, options) => {
    const { applicantBirthdate } = options;
    // validate acquired date when type is realestate
    if (isPVQForm(options?.formType)) {
      if (attributes?.FinancialInterestType?.value === REALESTATE) {
        return {
          presence: true,
          date: {
            requireDay: false,
            earliest: setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST),
            latest: DEFAULT_LATEST,
            tooEarly: 'error.applicantMin',
          },
        };
      }
      return {};
    }

    return {
      presence: true,
      date: {
        requireYear: false,
        earliest: applicantBirthdate,
        tooEarly: 'error.applicantMin',
      },
    };
  },

  HowAcquired: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {}; // not used in PVQ
    }
    return { hasValue: true };
  },

  Cost: (value, attributes, attributeName, options) => {
    // validate cost when type is realestate
    if (isPVQForm(options?.formType) && attributes?.FinancialInterestType?.value !== REALESTATE) {
      return {};
    }
    return {
      presence: true,
      model: { validator: currencyModelValidator }
    };
  },

  CostEstimated: {}, // not used in PVQ

  Value: (value, attributes, attributeName, options) => {
    // valiate when type is realestate and still owned.
    if (isPVQForm(options?.formType)) {
      if (attributes?.StillOwned?.value === 'No' || attributes?.FinancialInterestType?.value !== REALESTATE) {
        return {};
      }
    }
    return {
      presence: true,
      model: { validator: currencyModelValidator },
    };
  },

  ValueEstimated: {}, // not used in pvq

  Relinquished: (value, attributes, attributeName, options) => {
    const interestType = attributes?.FinancialInterestType?.value ?? '';

    if (attributes?.RelinquishedNotApplicable?.applicable === false
      || !attributes?.Acquired
      // validate date when type is realestate and not still owned
      || (isPVQForm(options?.formType)
        && (interestType !== REALESTATE
          || attributes?.StillOwned?.value === 'Yes'
        )
      )
    ) {
      return {};
    }

    return {
      presence: true,
      date: {
        earliest: attributes?.Acquired,
        requireDay: false,
        tooEarly: RELINQUISHED_DATE_TOO_EARLY,
        latest: DEFAULT_LATEST,
      },
    };
  },
  RelinquishedNotApplicable: {},

  Explanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) || attributes?.RelinquishedNotApplicable?.applicable === false) { return {}; } // not used in PVQ
    return { hasValue: true };
  },

  CoOwners: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.HasCoOwners?.value !== 'Yes') {
      return {};
    }
    return {
      presence: true,
      model: {
        validator: foreignCoOwnersModel,
      },
    };
  },

  // PVQ Only Fields below
  StillOwned: (value, attributes, attributeName, options) => {
    // validate when realestate
    if (isPVQForm(options?.formType) && attributes?.FinancialInterestType?.value === REALESTATE) {
      return { hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },

  // TODO: PVQ Validation
  InterestOwnerType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignInterestOwnerOptions,
            },
          },
        },
      };
    }
    return {};
  },
  DependentChildrenName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.InterestOwnerType?.value === 'Dependent children') {
      return {
        presence: true,
        model: { validator: nameModelValidator },
      };
    }
    return { };
  },
  HasCoOwners: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return { hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  FinancialInterestType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignFinancialInterestOptions,
            },
          },
        },
      };
    }
    return {};
  },
  FinancialInterestTypeOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.FinancialInterestType?.value === OTHR) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  RealEstateType: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.FinancialInterestType?.value === REALESTATE) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignRealEstateOptions,
            },
          },
        },
      };
    }
    return {};
  },
  RealEstateTypeOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.RealEstateType?.value === 'Other'
      && attributes?.FinancialInterestType?.value === REALESTATE) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  RealEstateTypeLocation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.FinancialInterestType?.value === REALESTATE) {
      return {
        presence: true,
        location: {
          validator: locationStreetCityCountryModelValidator,
          excludeUnitedStates: true,
        }
      };
    }
    return {};
  },
  HowRealEstateAcquired: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.FinancialInterestType?.value === REALESTATE) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignFinancialInterestHowAcquiredOptions,
            },
          },
        },
      };
    }
    return {};
  },
  HowAcquiredOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && attributes?.HowRealEstateAcquired?.value === 'Other'
      && attributes?.FinancialInterestType?.value === REALESTATE) {
      return { presence: true, hasValue: true };
    }
    return { };
  },
  RelinquishedValue: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.FinancialInterestType?.value === REALESTATE
  && attributes?.StillOwned?.value === 'No') {
      return { presence: true, hasValue: true };
    }
    return { };
  },

  Country: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && FINANCIAL_FOREIGN_INTEREST_VALUES.includes(attributes?.FinancialInterestType?.value || '')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  ForeignInterestAcquired: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && FINANCIAL_FOREIGN_INTEREST_VALUES.includes(attributes?.FinancialInterestType?.value || '')) {
      return {
        presence: true,
        date: {
          requireDay: false,
          earliest: setToStartOfMonth(options?.applicantBirthdate || DEFAULT_EARLIEST),
          tooEarly: 'error.applicantMin',
        },
      };
    }
    return {};
  },
  HowForeignInterest: (value, attributes, attributeName, options) => {
    if (isPvqForeignItem(attributes, options)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignFinancialInterestHowAcquiredOptions,
            },
          },
        },
      };
    }
    return { };
  },
  // start over here
  ForeignInterestOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPvqForeignItem(attributes, options) && attributes?.HowForeignInterest?.value?.includes('Other')) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  ForeignInterestPurchaseValue: (value, attributes, attributeName, options) => {
    if (isPvqForeignItem(attributes, options)) {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  ForeignInterestStillOwned: (value, attributes, attributeName, options) => {
    if (isPvqForeignItem(attributes, options)) {
      return { hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },

  ForeignInterestCurrentValue: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwned(attributes, options)) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  ForeignInterestSoldValue: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemNotStillOwned(attributes, options)) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  ForeignInterestHowRelinquished: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemNotStillOwned(attributes, options)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignFinancialInterestHowRelinquishedOptions,
            },
          },
        },
      };
    }
    return {};
  },

  ForeignInterestHowRelinquishedOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemNotStillOwned(attributes, options) && attributes?.ForeignInterestHowRelinquished?.value === 'Other') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  ForeignInterestSoldDate: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemNotStillOwned(attributes, options)) {
      return {
        presence: true,
        date: {
          requireDay: false,
          earliest: attributes?.ForeignInterestAcquired,
          tooEarly: RELINQUISHED_DATE_TOO_EARLY,
          latest: DEFAULT_LATEST,
        },
      };
    }
    return {};
  },
  ForeignInterestControl: (value, attributes, attributeName, options) => {
    if (isPvqForeignItem(attributes, options)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignFinancialInterestControllerOptions,
            },
          },
        },
      };
    }
    return { };
  },
  ForeignInterestControlOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPvqForeignItem(attributes, options) && attributes?.ForeignInterestControl?.value === 'Other') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  ForeignInterestRelationship: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwnedByPerson(attributes, options)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: foreignInterestCoOwnerOptions,
            },
          },
        },
      };
    }
    return { };
  },

  ForeignInterestRelationshipOtherExplanation: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwnedByPerson(attributes, options) && attributes?.ForeignInterestRelationship?.value === 'Other') {
      return { presence: true, hasValue: true };
    }
    return {};
  }, // not used

  ForeignInterestControllerName: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwnedByPerson(attributes, options)) {
      return {
        presence: true,
        model: { validator: nameModelValidator },
      };
    }
    return {};
  },
  ForeignInterestCountry: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwnedByPerson(attributes, options)
      && attributes?.ForeignInterestCountryNotApplicable?.applicable === true) {
      return { presence: true, hasValue: true };
    }

    return {};
  },
  ForeignInterestControlAddress: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwnedByPerson(attributes, options) && attributes?.ForeignInterestControlAddressNotApplicable?.applicable && attributes?.ForeignInterestControlAddressInUsaNotApplicable?.applicable) {
      // return {
      //   presence: true,
      //   location: {
      //     validator: pvqLocationModelValidator,
      //   },
      // };
      return {
        location: {
          requireIsMilitaryDiplomaticAddress: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireStreet: isSafeUnitedStates(value),
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          validator: locationAddressModelValidator,
        },
        presence: true,
      };
    }
    return {};
  },
  ForeignInterestAnotherControl: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwnedByCompany(attributes, options) || isPvqForeignItemStillOwnedByPerson(attributes, options)) {
      return { hasValue: { validator: hasYesOrNo } };
    }
    return {};
  },
  ForeignInterestAnotherControlExplanation: (value, attributes, attributeName, options) => {
    if ((isPvqForeignItemStillOwnedByCompany(attributes, options) || isPvqForeignItemStillOwnedByPerson(attributes, options))
       && attributes?.ForeignInterestAnotherControl?.value === 'Yes') {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  ForeignInterestControlCompanyName: (value, attributes, attributeName, options) => {
    if (isPvqForeignItemStillOwnedByCompany(attributes, options)) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
};
